import { Link } from "react-router";

import { LoadingNote } from "../lib/Loading.tsx";
import { RoomLink } from "./RoomList.tsx";
import { VIEWING_ROOMS_COPY } from "./viewingRooms.constants.ts";
import { RoomWorksPreview } from "./RoomWorksPreview.tsx";
import type { PreviewView, RoomRow, VisitRow } from "./viewingRooms.model.ts";
import { Tag } from "../lib/Tag.tsx";

/**
 * Who has been in.
 *
 * The unverified label is not decoration. Half these rows are a string somebody
 * typed into a box on a public page, and a list that presented them beside a
 * verified ONDO account without saying which was which would be inviting the
 * reader to treat both as identities.
 */
export function RoomAudit({ visits }: { visits: VisitRow[] | null }) {
  return (
    <section className="rooms__audit">
      <h3 className="rooms__legend">{VIEWING_ROOMS_COPY.visitsTitle}</h3>

      {visits === null && <LoadingNote message={VIEWING_ROOMS_COPY.visitsLoading} />}
      {visits !== null && visits.length === 0 && (
        <p className="rooms__note">{VIEWING_ROOMS_COPY.visitsEmpty}</p>
      )}

      {visits !== null && visits.length > 0 && (
        <ul className="rooms__audit-list">
          {visits.map((visit) => (
            <li key={visit.id} className="rooms__audit-row">
              <span className="rooms__audit-who">{visit.who}</span>
              <Tag tone={visit.member ? "verified" : "quiet"}>
                {visit.member
                  ? VIEWING_ROOMS_COPY.visitsMember
                  : VIEWING_ROOMS_COPY.visitsUnverified}
              </Tag>
              {visit.opens && <span className="rooms__audit-when">{visit.opens}</span>}
              <span className="rooms__audit-when">{visit.when}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="rooms__hint">{VIEWING_ROOMS_COPY.visitsNote}</p>
    </section>
  );
}

export function RoomDetail({
  row,
  visits,
  backHref,
  busy,
  error,
  copied,
  confirming,
  onCopy,
  onHide,
  onAskDelete,
  onDelete,
  onEditRoom,
  preview,
}: {
  row: RoomRow;
  visits: VisitRow[] | null;
  backHref: string;
  busy: boolean;
  error: string | null;
  copied: boolean;
  confirming: boolean;
  onCopy: (roomId: string, url: string) => void;
  onHide: (roomId: string, hidden: boolean) => void;
  onAskDelete: (roomId: string | null) => void;
  onDelete: (roomId: string) => void;
  onEditRoom: (roomId: string) => void;
  preview: PreviewView | null;
}) {
  return (
    <section className="rooms rooms__detail">
      <Link to={backHref} className="rooms__back">
        {VIEWING_ROOMS_COPY.back}
      </Link>

      <h2 className="rooms__name">{row.title}</h2>
      <p className="rooms__meta">{row.meta}</p>
      <p className="rooms__shows">{row.shows}</p>

      {row.canCopy ? (
        <RoomLink url={row.url} copied={copied} onCopy={() => onCopy(row.id, row.url)} />
      ) : (
        <p className="rooms__note">{VIEWING_ROOMS_COPY.hiddenNote}</p>
      )}

      {error && (
        <p className="rooms__error" role="alert">
          {error}
        </p>
      )}

      <div className="rooms__row-actions">
        <button
          type="button"
          className="rooms__action"
          disabled={busy}
          onClick={() => onEditRoom(row.id)}
        >
          {VIEWING_ROOMS_COPY.edit}
        </button>
        <button
          type="button"
          className="rooms__action"
          disabled={busy}
          onClick={() => onHide(row.id, !row.hidden)}
        >
          {row.hidden ? VIEWING_ROOMS_COPY.reopen : VIEWING_ROOMS_COPY.hide}
        </button>

        {confirming ? (
          <>
            <button
              type="button"
              className="rooms__danger"
              disabled={busy}
              onClick={() => onDelete(row.id)}
            >
              {VIEWING_ROOMS_COPY.removeConfirmAction}
            </button>
            <button type="button" className="rooms__action" onClick={() => onAskDelete(null)}>
              {VIEWING_ROOMS_COPY.keep}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="rooms__action"
            onClick={() => onAskDelete(row.id)}
          >
            {VIEWING_ROOMS_COPY.remove}
          </button>
        )}
      </div>

      {confirming && (
        <p className="rooms__confirm" role="alert">
          {VIEWING_ROOMS_COPY.removeConfirmDetail}
        </p>
      )}

      <RoomWorksPreview preview={preview} note={VIEWING_ROOMS_COPY.previewLiveNote} />

      <RoomAudit visits={visits} />
    </section>
  );
}
